import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Divider,
  Breadcrumbs,
  Link,
  Chip,
  Card,
  CardContent,
  Button,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SeoHead from '../components/common/SeoHead';
import LazyImage from '../components/common/LazyImage';
import SocialShare from '../components/common/SocialShare';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReactMarkdown from 'react-markdown';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';

// 导入图片
import amethystImg from '../assets/images/index/cuR2.jpg';
import crystalImg from '../assets/images/index/cuR1.png';
import fortuneImg from '../assets/images/Luopan/banner1.png';
import tarotImg from '../assets/images/Luopan/L3.png';
import baziImg from '../assets/images/Luopan/L1.png';

// 默认图片映射
const defaultImages = {
  'crystal': amethystImg,
  'fortune': fortuneImg,
  'yijing': baziImg, // 使用八卦相关图片作为易经文章的默认图片
  'default': crystalImg
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  
  // 提取文章目录
  const extractHeadings = (content) => {
    const headings = [];
    const h2Regex = /## (.*?)(\n|$)/g;
    const h3Regex = /### (.*?)(\n|$)/g;
    
    let match;
    while ((match = h2Regex.exec(content)) !== null) {
      headings.push({ text: match[1].trim(), level: 2 });
    }
    
    while ((match = h3Regex.exec(content)) !== null) {
      headings.push({ text: match[1].trim(), level: 3 });
    }
    
    // 截断过长的标题
    return headings.map(heading => ({
      ...heading,
      text: heading.text.length > 30 ? heading.text.substring(0, 30) + '...' : heading.text
    }));
  };
  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 调用云函数获取文章详情
        const getArticleById = httpsCallable(functions, 'getSEOArticleById');
        console.log('正在调用getSEOArticleById云函数，文章ID:', slug);
        let result;
        try {
          result = await getArticleById({ articleId: slug });
        } catch (funcError) {
          console.warn('无法获取文章详情，错误:', funcError);
          throw new Error('获取文章详情失败: ' + funcError.message);
        }
        
        if (result.data && result.data.success && result.data.article) {
          const article = result.data.article;
          // 详细记录文章字段，帮助调试
          console.log('获取到文章数据:', article.id);
          console.log('字段列表:', Object.keys(article).join(', '));
          console.log('标题:', article.title);
          console.log('类型:', article.type);
          console.log('状态:', article.status);
          console.log('内容长度:', article.content ? article.content.length : 0);
          
          // 确保content存在，避免空指针异常
          const content = article.content || '';
          
          // 直接使用Firestore中存储的title字段
          let title = article.title || '无标题文章';
          
          // 提取摘要
          const excerptLength = 200;
          let excerpt = '';
          if (content) {
            excerpt = content.replace(/#/g, '').substring(0, excerptLength);
            if (content.length > excerptLength) {
              excerpt += '...';
            }
          } else {
            excerpt = title + '的详细介绍...';
          }
          
          // 提取标签
          let tags = [];
          if (content) {
            const tagMatch = content.match(/关键词：(.*?)(\n|$)/);
            if (tagMatch) {
              tags = tagMatch[1].split('、').map(tag => tag.trim());
            }
          }
          
          // 如果没有标签，基于类型设置默认标签
          if (tags.length === 0) {
            tags = article.type === 'crystal' 
              ? ['水晶能量', '能量', '水晶'] 
              : ['命理', '运势', '八字'];
          }
          
          // 提取目录结构
          const headings = content ? extractHeadings(content) : [];
          
          // 确定图片和类别 - 支持易经八卦类型
          const category = article.type === 'crystal' ? 'crystal' : 
                           article.type === 'fortune' ? 'fortune' : 
                           article.type === 'yijing' ? 'yijing' : 'default';
          const image = defaultImages[category];
          
          // 保存语言信息以供后续使用
          const language = article.language || 'chinese';
          
          // 格式化日期 - 安全处理
          let dateStr;
          try {
            // 检查createdAt是否已经是Date对象
            if (article.createdAt instanceof Date) {
              dateStr = article.createdAt.toISOString().split('T')[0];
            } else if (typeof article.createdAt === 'object' && article.createdAt !== null) {
              // 可能是Firestore Timestamp
              console.log('文章详情 createdAt类型:', typeof article.createdAt, article.createdAt);
              // 检查是否有seconds属性(Firestore Timestamp特性)
              if (article.createdAt.seconds) {
                const timestamp = new Date(article.createdAt.seconds * 1000);
                dateStr = timestamp.toISOString().split('T')[0];
              } else {
                // 使用当前日期作为后备
                dateStr = new Date().toISOString().split('T')[0];
              }
            } else {
              // 使用当前日期作为后备
              dateStr = new Date().toISOString().split('T')[0];
            }
          } catch (dateError) {
            console.warn('文章详情 日期格式化错误:', dateError);
            dateStr = new Date().toISOString().split('T')[0];
          }
          
          // 设置文章
          setPost({
            id: article.id,
            title: title,
            author: 'DestinyCrystal命理水晶团队',
            date: dateStr,
            updateDate: dateStr, // 使用相同的安全处理后的日期
            category: category,
            image: image,
            readTime: Math.ceil(content.length / 500) || 3, // 估算阅读时间，至少3分钟
            tags: tags,
            excerpt: excerpt,
            content: content,
            viewCount: article.viewCount || 0,
            headings: headings,
            type: article.type, // 保存文章类型
            language: language // 保存文章语言
          });
          
          // 获取相关文章
          fetchRelatedArticles(article.type);
        } else {
          setError('获取文章失败');
          // 如果找不到文章，重定向到博客列表页
          setTimeout(() => navigate('/blog'), 2000);
        }
      } catch (err) {
        console.error('获取文章数据错误:', err);
        setError('获取文章数据时出错，请稍后再试');
        setTimeout(() => navigate('/blog'), 2000);
      } finally {
        setLoading(false);
      }
    };
    
    // 获取相关文章
    const fetchRelatedArticles = async (type) => {
      try {
        const getArticles = httpsCallable(functions, 'getLatestSEOArticles');
        // 获取文章的语言（如果存在）
        const language = post ? post.language : 'chinese';
        
        // 调用API时包含语言参数
        const result = await getArticles({ 
          limit: 5, // 获取更多文章以便筛选
          type: type,
          language: language // 添加语言参数
        });
        
        if (result.data && result.data.success && result.data.articles) {
          // 过滤掉当前文章，优先展示相同语言的文章
          const articles = result.data.articles
            .filter(article => article.id !== slug)
            .slice(0, 2) // 只取2篇
            .map(article => {
              console.log('相关文章数据:', article.id, Object.keys(article).join(', '));
              
              // 确保content存在，避免空指针异常
              const content = article.content || '';
              
              // 优先使用article.title，如果不存在则尝试从content中提取
              let title = article.title || '';
              if (!title && content) {
                const titleMatch = content.match(/# (.*?)(\n|$)/);
                title = titleMatch ? titleMatch[1].trim() : '水晶能量文章';
              }
              
              // 提取摘要
              const excerptLength = 100;
              let excerpt = '';
              if (content) {
                excerpt = content.replace(/#/g, '').substring(0, excerptLength);
                if (content.length > excerptLength) {
                  excerpt += '...';
                }
              } else {
                excerpt = title + '的详细介绍...';
              }
              
              // 确定图片
              // 更新确定类别的逻辑，支持易经八卦分类
              const category = article.type === 'crystal' ? 'crystal' : 
                               article.type === 'fortune' ? 'fortune' : 
                               article.type === 'yijing' ? 'yijing' : 'default';
              const image = defaultImages[category];
              
              return {
                id: article.id,
                title: title || '相关文章',
                slug: article.id,
                excerpt: excerpt,
                image: image
              };
            });
          
          setRelatedArticles(articles);
        }
      } catch (err) {
        console.error('获取相关文章错误:', err);
        // 不设置错误，相关文章不是必须的
      }
    };
    
    if (slug) {
      fetchArticle();
    }
  }, [slug, navigate]);
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>正在加载文章...</Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Button 
          component={RouterLink} 
          to="/blog" 
          variant="contained" 
          color="primary"
          sx={{ mt: 2 }}
        >
          返回博客列表
        </Button>
      </Container>
    );
  }
  
  if (!post) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <Typography>未找到文章</Typography>
        <Button 
          component={RouterLink} 
          to="/blog" 
          variant="contained" 
          color="primary"
          sx={{ mt: 2 }}
        >
          返回博客列表
        </Button>
      </Container>
    );
  }
  
  // 文章结构化数据
  const articleSchemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": `https://destiny-crystal.com/assets/images/${post.image}`,
    "author": {
      "@type": "Organization",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶",
      "logo": {
        "@type": "ImageObject",
        "url": "https://destiny-crystal.com/assets/images/logo.png"
      }
    },
    "datePublished": post.date,
    "dateModified": post.updateDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://destiny-crystal.com/blog/${slug}`
    },
    "keywords": post.tags.join(', ')
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* SEO优化 */}
      <SeoHead
        title={`${post.title} | DestinyCrystal博客`}
        description={post.excerpt}
        canonicalUrl={`https://destiny-crystal.com/blog/${slug}`}
        imageUrl={`https://destiny-crystal.com/assets/images/${post.image}`}
        keywords={post.tags}
        schemaData={articleSchemaData}
      />
      
      {/* 面包屑导航 */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" color="inherit">
          首页
        </Link>
        <Link component={RouterLink} to="/blog" color="inherit">
          博客
        </Link>
        <Typography color="text.primary">{post.title}</Typography>
      </Breadcrumbs>
      
      {/* 文章主体 */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            {/* 文章标题区域 */}
            <Box mb={4}>
              <Box sx={{ display: 'flex', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                {post.tags.map((tag, index) => (
                  <Chip 
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
              
              <Typography variant="h4" component="h1" gutterBottom>
                {post.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                  {post.author.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">{post.author}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    发布于 {post.date} • 阅读时间 {post.readTime} 分钟
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 4 }} />
            </Box>
            
            {/* 文章特色图片 */}
            <Box sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
              <LazyImage
                src={post.image}
                alt={post.title}
                sx={{ width: '100%', height: 'auto', maxHeight: 400 }}
              />
            </Box>
            
            {/* 文章摘要 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {post.excerpt}
              </Typography>
            </Box>
            
            {/* 文章内容 - 使用ReactMarkdown代替简单的HTML转换 */}
            <Box component="article" itemScope itemType="http://schema.org/BlogPosting" sx={{ 
              '& h2': { 
                mt: 4, 
                mb: 2, 
                pb: 1, 
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              },
              '& h3': { 
                mt: 3, 
                mb: 2,
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'primary.main'
              },
              '& p': {
                mb: 2,
                lineHeight: 1.7
              },
              '& ul': {
                pl: 2,
                mb: 2
              },
              '& li': {
                mb: 1
              },
              '& strong': {
                fontWeight: 'bold',
                color: 'text.primary'
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.light',
                pl: 2,
                py: 1,
                my: 2,
                bgcolor: 'rgba(0,0,0,0.02)',
                fontStyle: 'italic'
              },
              '& code': {
                fontFamily: 'monospace',
                bgcolor: 'rgba(0,0,0,0.05)',
                p: 0.5,
                borderRadius: 0.5
              }
            }}>
              <meta itemProp="author" content="DestinyCrystal命理水晶团队" />
              <meta itemProp="datePublished" content={post.date} />
              <meta itemProp="publisher" content="DestinyCrystal" />
              <meta itemProp="headline" content={post.title} />
              <meta itemProp="image" content={post.image} />
              <ReactMarkdown>
                {post.content}
              </ReactMarkdown>
            </Box>
            
            {/* 文章标签 */}
            <Box mt={6} mb={4}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="subtitle2" gutterBottom>
                标签:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {post.tags.map((tag, index) => (
                  <Chip 
                    key={index}
                    label={tag}
                    component={RouterLink}
                    to={`/blog?tag=${tag}`}
                    clickable
                    size="small"
                    sx={{ fontSize: '0.8rem' }}
                  />
                ))}
              </Box>
            </Box>
            
            {/* 分享和评论 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                最后更新: {post.updateDate}
              </Typography>
              <Box>
                <SocialShare 
                  url={`https://destiny-crystal.com/blog/${slug}`}
                  title={post.title}
                  description={post.excerpt}
                  image={`https://destiny-crystal.com/assets/images/${post.image}`}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* 侧边栏 */}
        <Grid item xs={12} md={4}>
          {/* 作者介绍 */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              关于作者
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}>
                {post.author.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1">{post.author}</Typography>
                <Typography variant="body2" color="text.secondary">
                  专业命理学与水晶能量研究团队
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2">
              DestinyCrystal团队由经验丰富的命理师、水晶能量治疗师和自然疗法专家组成，致力于将传统东方智慧与现代自然疗法相结合，帮助人们获得平衡和谐的生活。
            </Typography>
          </Paper>
          
          {/* 相关文章 */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              相关阅读
            </Typography>
            
            {relatedArticles.length > 0 ? (
              relatedArticles.map((relatedPost) => (
                <Box key={relatedPost.id} sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ borderRadius: 1, overflow: 'hidden' }}>
                        <LazyImage
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          sx={{ height: 70 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="subtitle2" component={RouterLink} to={`/blog/${relatedPost.slug}`} sx={{ 
                        textDecoration: 'none', 
                        color: 'text.primary',
                        '&:hover': {
                          color: 'primary.main'
                        },
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {relatedPost.title}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                暂无相关文章
              </Typography>
            )}
          </Paper>
          
          {/* 目录 - 动态生成目录结构 */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              文章目录
            </Typography>
            {post.headings && post.headings.length > 0 ? (
              <List dense>
                {post.headings.map((heading, index) => (
                  <ListItem key={index} sx={{ pl: heading.level === 3 ? 2 : 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircleOutlineIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={heading.text} 
                      primaryTypographyProps={{
                        fontWeight: heading.level === 2 ? 'medium' : 'normal',
                        fontSize: heading.level === 2 ? '0.9rem' : '0.85rem'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                暂无目录结构
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* 推荐阅读 */}
      <Box mt={8}>
        <Typography variant="h5" gutterBottom align="center">
          你可能也喜欢
        </Typography>
        <Divider sx={{ width: 60, margin: '0 auto', mb: 4, borderColor: 'primary.main', borderWidth: 2 }} />
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            component={RouterLink}
            to="/blog"
            variant="contained"
            color="primary"
            size="large"
          >
            浏览更多文章
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default BlogPost;